import RemoteDiscSource from './RemoteDiscSource';

export default class DiscDevice {
    module: any;
    doneFlag: Boolean;
    file: File | null;
    /**
     * Image servie par le serveur, lue par plages HTTP. Exclusive avec `file` :
     * setFile et setRemote effacent l'autre source.
     */
    remote: RemoteDiscSource | null;

    constructor(module: any) {
        this.module = module;
        this.doneFlag = false;
        this.file = null;
        this.remote = null;
    }

    /**
     * Lit une portion de l'image disque vers la memoire WASM.
     *
     * readyPtr designe un int32 dans la memoire partagee, sur lequel le worker
     * de la VM attend via Atomics.wait. On le leve et on notifie des que la
     * copie est faite : le worker repart immediatement, sans scrutation.
     *
     * L'ancien mecanisme (drapeau JavaScript interroge en boucle depuis le
     * worker) coutait une trame d'affichage par tour sous Firefox -- 77 s
     * d'attente sur 86 s de session mesuree.
     */
    read(dstPtr: number, offset: number, size: number, readyPtr?: number) {
        this.doneFlag = false;

        // Les deux sources aboutissent au meme traitement : produire les octets,
        // les copier dans le tas WASM, puis reveiller le worker. Seule la facon
        // de les obtenir differe.
        let bytes: Promise<Uint8Array>;
        if(this.remote) {
            bytes = this.remote.read(offset, size);
        } else if(this.file) {
            bytes = this.file.slice(offset, offset + size).arrayBuffer()
                .then((value: ArrayBuffer) => new Uint8Array(value));
        } else {
            throw new Error("No file set.");
        }

        bytes.then((value: Uint8Array) => {
            this.module.HEAPU8.set(value, dstPtr);
            this.doneFlag = true;
            this.signal(readyPtr);
        }).catch(() => {
            // Une lecture qui echoue doit tout de meme reveiller le worker,
            // sinon la VM reste bloquee jusqu'a expiration du delai.
            this.doneFlag = true;
            this.signal(readyPtr);
        });
    }

    /** Leve le drapeau partage et reveille le worker en attente. */
    private signal(readyPtr?: number) {
        if(readyPtr === undefined || readyPtr === 0) return;
        const heap = this.module.HEAP32;
        // HEAP32 est indexe en mots de 4 octets.
        const index = readyPtr >> 2;
        if(typeof Atomics !== "undefined" && heap.buffer instanceof SharedArrayBuffer) {
            Atomics.store(heap, index, 1);
            Atomics.notify(heap, index);
        } else {
            // Repli sans memoire partagee : le worker retombe sur son delai.
            heap[index] = 1;
        }
    }

    getFileSize() {
        if(this.remote) {
            return this.remote.size;
        }
        if(!this.file) {
            throw new Error("No file set.");
        }
        return this.file.size;
    }

    isDone() {
        return this.doneFlag;
    }

    setFile(file : File) {
        this.file = file;
        this.remote = null;
    }

    /** Bascule sur une image servie par le serveur, lue a la demande. */
    setRemote(remote : RemoteDiscSource) {
        this.remote = remote;
        this.file = null;
    }
};
